import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useContext, useState, useRef, useEffect, useImperativeHandle, Fragment } from 'react';
import classnames from 'classnames';
import { throttle } from '@hh.ru/magritte-common-func-utils';
import { CustomScrollContext } from '@hh.ru/magritte-internal-custom-scroll';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { s as styles, N as NavigationBarItem } from './NavigationBarItem-BQ6pdgfa.js';
import { NavigationBarItems } from './NavigationBarItems.js';
import { ProgressBar } from '@hh.ru/magritte-ui-progress-bar';
import '@hh.ru/magritte-ui-action';
import '@hh.ru/magritte-ui-link';
import '@hh.ru/magritte-ui-icon/icon';
import './types.js';

// ключи нужны для того чтобы не было ремаунта options при изменении верстки с large на standard
const ITEMS_CONTAINER_KEY = 'items-container';
const TITLE_CONTAINER_KEY = 'title-container';
const OPTIONS_CONTAINER_KEY = 'options-container';
const OPTIONS_KEY = 'options';
const DUMMY_KEY = 'dummy';
const SCROLL_THROTTLE_MS = 50;
const calcTitleOpacity = (opacity) => `${Math.pow(opacity, 2)}`;
const calcDummyOpacity = (opacity) => `${1 - 2 * opacity}`;
const findScrollContainer = (element) => {
    if (!element) {
        return null;
    }
    let parentElement = element.parentElement;
    while (parentElement !== null) {
        const { overflowY } = window.getComputedStyle(parentElement);
        if (overflowY.includes('auto') || overflowY.includes('scroll')) {
            return parentElement;
        }
        parentElement = parentElement.parentElement;
    }
    return null;
};
const renderStringNode = (node, actualSize) => {
    return typeof node === 'function' ? node(actualSize) : node;
};
const StandardTitle = ({ title, subtitle, left, right, ellipsisRenderFunc, 'data-qa': dataQa, }) => {
    return (jsxs("div", { className: styles.spread, "data-qa": dataQa, role: "menu", children: [jsx("div", { className: styles.left, children: left && jsx(NavigationBarItem, { role: "menuitem", item: left }) }), jsxs("div", { className: styles.right, children: [jsx("div", { className: styles.halfTitleWrapper, children: jsx("div", { className: styles.titleWrapper, children: title && (jsxs(Fragment, { children: [jsx("div", { className: classnames(styles.title, {
                                            [styles.titleWithoutSubtitle]: !subtitle,
                                        }), children: renderStringNode(title, 'standard') }), subtitle && (jsx("div", { className: styles.subtitle, children: renderStringNode(subtitle, 'standard') }))] })) }) }), jsx("div", { className: styles.rightDummy, children: jsx(NavigationBarItems, { items: right, size: "standard", renderFunc: ellipsisRenderFunc }) })] })] }));
};
const NavigationBarLayer = ({ children }) => {
    return jsx(Layer, { layer: "navigation-bar", children: children });
};
const NavigationBarComponent = ({ size = 'standard', title, subtitle, left, progress, right, options, allowOptionsScroll, showDivider: showDividerMode, ellipsisRenderFunc, inLayer, forwardedRef, }) => {
    const scrollContext = useContext(CustomScrollContext);
    const [actualSize, setActualSize] = useState(size);
    const [showDivider, setShowDivider] = useState(showDividerMode === 'always');
    const stateRef = useRef({
        actualSize: size,
        heightCompensation: 0,
        heightObservers: [],
        heightWithLargeSize: null,
        heightWithStandardSize: 0,
        titleTranslateY: 0,
        optionsTranslateY: 0,
        transitionProgress: 1,
        skipFirstResizeCallback: true,
    });
    const itemsContainerRef = useRef(null);
    const titleContainerRef = useRef(null);
    const optionsContainerRef = useRef(null);
    const largeTitleRef = useRef(null);
    const dividerRef = useRef(null);
    const dummyRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const scrollContainerRef = useRef();
    useEffect(() => {
        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (stateRef.current.skipFirstResizeCallback) {
                    stateRef.current.skipFirstResizeCallback = false;
                    return;
                }
                stateRef.current.heightObservers.forEach((observer) => observer());
            });
        }
        const resizeObserver = resizeObserverRef.current;
        const titleContainer = titleContainerRef.current;
        const optionsContainer = optionsContainerRef.current;
        titleContainer !== null && resizeObserver.observe(titleContainer);
        optionsContainer !== null && resizeObserver.observe(optionsContainer);
        return () => {
            titleContainer !== null && resizeObserver.unobserve(titleContainer);
            optionsContainer !== null && resizeObserver.unobserve(optionsContainer);
        };
    }, [actualSize]);
    useEffect(() => {
        setShowDivider(showDividerMode === 'always');
    }, [showDividerMode]);
    useEffect(() => {
        const itemsContainer = itemsContainerRef.current;
        const titleContainer = titleContainerRef.current;
        const titleContainerHeight = titleContainer?.clientHeight ?? 0;
        const optionsContainer = optionsContainerRef.current;
        const optionsContainerHeight = optionsContainer?.clientHeight ?? 0;
        if (actualSize === 'large') {
            const itemsContainerHeight = itemsContainer?.clientHeight ?? 0;
            const dummyHeight = dummyRef.current?.clientHeight ?? 0;
            stateRef.current.heightWithLargeSize = itemsContainerHeight + titleContainerHeight + optionsContainerHeight;
            stateRef.current.heightWithStandardSize = dummyHeight + optionsContainerHeight;
        }
        if (actualSize === 'standard') {
            stateRef.current.heightWithStandardSize = titleContainerHeight + optionsContainerHeight;
        }
        const handleScroll = (scrollTop, maxScrollTop) => {
            if (size === 'large' &&
                stateRef.current.heightWithLargeSize !== null &&
                stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize <= maxScrollTop) {
                const visibleHeight = stateRef.current.heightWithLargeSize - scrollTop;
                const _transitionProgress = (visibleHeight - stateRef.current.heightWithStandardSize) /
                    (stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize);
                const transitionProgress = Math.max(Math.min(_transitionProgress, 1), 0);
                if (Math.abs(stateRef.current.transitionProgress - transitionProgress) > Number.EPSILON) {
                    stateRef.current.transitionProgress = Math.max(Math.min(transitionProgress, 1), 0);
                    if (itemsContainer !== null) {
                        itemsContainer.style.setProperty('--navigation-bar-transition-opacity', calcTitleOpacity(transitionProgress));
                    }
                    if (largeTitleRef.current !== null) {
                        largeTitleRef.current.style.opacity = calcTitleOpacity(transitionProgress);
                    }
                    if (dividerRef.current !== null) {
                        dividerRef.current.style.opacity = calcDummyOpacity(transitionProgress);
                    }
                    if (dummyRef.current !== null) {
                        dummyRef.current.style.opacity = calcDummyOpacity(transitionProgress);
                    }
                }
                if (actualSize === 'large' && visibleHeight < stateRef.current.heightWithStandardSize) {
                    stateRef.current.actualSize = 'standard';
                    stateRef.current.heightCompensation =
                        stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize;
                    setActualSize('standard');
                }
                if (actualSize === 'standard' && visibleHeight > stateRef.current.heightWithStandardSize) {
                    stateRef.current.actualSize = 'large';
                    stateRef.current.heightCompensation = 0;
                    setActualSize('large');
                }
            }
            if ((size === 'standard' || stateRef.current.actualSize === 'standard') &&
                showDividerMode === 'with-scroll') {
                const visibleHeight = stateRef.current.heightWithStandardSize - scrollTop;
                setShowDivider(Math.abs(stateRef.current.heightWithStandardSize - visibleHeight) > Number.EPSILON);
            }
        };
        if (scrollContext !== null) {
            const handleCustomScroll = (event) => {
                handleScroll(event.scrollTop, event.maxScrollTop);
                const applyTransform = stateRef.current.actualSize === actualSize;
                if (titleContainer !== null) {
                    const titleTranslateY = stateRef.current.actualSize === 'large'
                        ? -event.scrollTop
                        : -stateRef.current.heightCompensation;
                    if (Math.abs(stateRef.current.titleTranslateY - titleTranslateY) > Number.EPSILON) {
                        stateRef.current.titleTranslateY = titleTranslateY;
                        if (applyTransform) {
                            titleContainer.style.transform = `translate3d(0, ${titleTranslateY}px, 0)`;
                        }
                    }
                }
                if (optionsContainerRef.current !== null) {
                    const maxOptionsTranslateY = stateRef.current.heightCompensation + optionsContainerHeight - (showDivider ? 1 : 0);
                    const optionsTranslateY = stateRef.current.actualSize === 'large'
                        ? -event.scrollTop
                        : -(allowOptionsScroll
                            ? Math.min(event.scrollTop, maxOptionsTranslateY)
                            : stateRef.current.heightCompensation);
                    if (Math.abs(stateRef.current.optionsTranslateY - optionsTranslateY) > Number.EPSILON) {
                        stateRef.current.optionsTranslateY = optionsTranslateY;
                        if (applyTransform) {
                            optionsContainerRef.current.style.transform = `translate3d(0, ${optionsTranslateY}px, 0)`;
                        }
                    }
                }
            };
            scrollContext.subscribe(handleCustomScroll);
            return () => {
                scrollContext.unsubscribe(handleCustomScroll);
            };
        }
        if (scrollContainerRef.current === undefined) {
            scrollContainerRef.current = findScrollContainer(titleContainer);
        }
        if (scrollContainerRef.current !== null) {
            const scrollContainer = scrollContainerRef.current;
            const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;
            const handleNativeScroll = throttle(() => handleScroll(scrollContainer.scrollTop, maxScrollTop), SCROLL_THROTTLE_MS);
            scrollContainer.addEventListener('scroll', handleNativeScroll);
            return () => {
                handleNativeScroll.cancel();
                scrollContainer.removeEventListener('scroll', handleNativeScroll);
            };
        }
        const maxScrollTop = document.documentElement.scrollHeight - visualViewport.height;
        const handleNativeScroll = throttle(() => handleScroll(visualViewport.pageTop, maxScrollTop), SCROLL_THROTTLE_MS);
        window.addEventListener('scroll', handleNativeScroll);
        return () => {
            handleNativeScroll.cancel();
            window.removeEventListener('scroll', handleNativeScroll);
        };
    }, [size, actualSize, setActualSize, allowOptionsScroll, showDivider, showDividerMode, scrollContext]);
    useImperativeHandle(forwardedRef, () => {
        return {
            /**
             * Фактическая высота, которую компонент занимает в родительском контейнере
             * Может отличаться от видимой высоты за счет marginTop (stateRef.current.heightCompensation)
             */
            get layoutHeight() {
                return stateRef.current.heightWithLargeSize ?? stateRef.current.heightWithStandardSize;
            },
            /**
             * Высота видимой части компонента
             * Может отличаться от фактической высоты
             */
            get visibleHeight() {
                return ((stateRef.current.actualSize === 'large' ? stateRef.current.heightWithLargeSize : null) ??
                    stateRef.current.heightWithStandardSize);
            },
            addHeightObserver: (observer) => {
                stateRef.current.heightObservers.push(observer);
            },
            removeHeightObserver: (observer) => {
                stateRef.current.heightObservers = stateRef.current.heightObservers.filter((_observer) => _observer !== observer);
            },
        };
    }, []);
    const Wrapper = inLayer ? NavigationBarLayer : Fragment;
    const titleProps = { title, subtitle, left, progress, right, ellipsisRenderFunc };
    const translateY = (value) => (scrollContext !== null ? `translate3d(0, ${value}px, 0)` : undefined);
    if (actualSize === 'large') {
        return (jsxs(Wrapper, { children: [jsx("div", { className: classnames(styles.fixedItemsContainer, styles.backgroundContainer), ref: itemsContainerRef, style: {
                        '--navigation-bar-transition-opacity': calcTitleOpacity(stateRef.current.transitionProgress),
                    }, children: jsx("div", { className: styles.topPaddingOverride, children: jsxs("div", { className: styles.spread, "data-qa": "navigation-bar-items", role: "menu", children: [jsx("div", { className: styles.left, children: left && jsx(NavigationBarItem, { role: "menuitem", item: left }) }), jsxs("div", { className: styles.right, children: [progress && (jsx("div", { className: classnames(styles.progress, styles.transitionWithOpacity), children: jsx("div", { className: styles.availableWidthWrapper, children: jsx(ProgressBar, { ...progress }) }) })), jsx(NavigationBarItems, { items: right, size: "large", renderFunc: ellipsisRenderFunc })] })] }) }) }, ITEMS_CONTAINER_KEY), jsx("div", { className: classnames(styles.scrollableTitleContainer), "data-qa": "navigation-bar-container navigation-bar-container_large", ref: titleContainerRef, style: { transform: translateY(stateRef.current.titleTranslateY) }, children: jsx("div", { className: classnames(styles.largeTitleContainer), ref: largeTitleRef, style: { opacity: calcTitleOpacity(stateRef.current.transitionProgress) }, children: title && (jsxs(Fragment, { children: [jsx("div", { className: styles.title, children: renderStringNode(title, actualSize) }), subtitle && (jsx("div", { className: styles.subtitle, children: renderStringNode(subtitle, actualSize) }))] })) }) }, TITLE_CONTAINER_KEY), options && (jsxs("div", { className: classnames(styles.scrollableOptionsContainer, styles.backgroundContainer), "data-qa": "navigation-bar-options", ref: optionsContainerRef, style: { transform: translateY(stateRef.current.optionsTranslateY) }, children: [jsx("div", { className: styles.options, children: options }, OPTIONS_KEY), showDividerMode && (jsx("div", { ref: dividerRef, style: { opacity: calcDummyOpacity(stateRef.current.transitionProgress) }, children: jsx(Divider, {}) }))] }, OPTIONS_CONTAINER_KEY)), jsx("div", { className: styles.dummy, ref: dummyRef, style: {
                        opacity: calcDummyOpacity(stateRef.current.transitionProgress),
                        position: scrollContext !== null ? 'absolute' : 'fixed',
                    }, children: jsx("div", { className: styles.topPaddingOverride, children: jsx("div", { className: classnames(styles.standardTitleContainer), children: jsx(StandardTitle, { ...titleProps }) }) }) }, DUMMY_KEY)] }));
    }
    if (actualSize === 'standard') {
        return (jsxs(Wrapper, { children: [jsx("div", {}, ITEMS_CONTAINER_KEY), jsx("div", { className: classnames(styles.fixedTitleContainer, styles.backgroundContainer), "data-qa": "navigation-bar-container navigation-bar-container_standard", ref: titleContainerRef, style: {
                        marginTop: `${stateRef.current.heightCompensation}px`,
                        transform: translateY(stateRef.current.titleTranslateY),
                    }, children: jsx("div", { className: styles.topPaddingOverride, children: jsx("div", { className: classnames(styles.standardTitleContainer), children: jsx(StandardTitle, { ...titleProps, "data-qa": "navigation-bar-items" }) }) }) }, TITLE_CONTAINER_KEY), allowOptionsScroll && (jsxs("div", { className: classnames(styles.scrollableOptionsContainer, styles.backgroundContainer), "data-qa": "navigation-bar-options", ref: optionsContainerRef, style: { transform: translateY(stateRef.current.optionsTranslateY) }, children: [options && (jsx("div", { className: styles.options, children: options }, OPTIONS_KEY)), showDivider && jsx(Divider, {})] }, OPTIONS_CONTAINER_KEY)), !allowOptionsScroll && (jsxs("div", { className: classnames(styles.fixedOptionsContainer, styles.backgroundContainer), "data-qa": "navigation-bar-options", ref: optionsContainerRef, style: { transform: translateY(stateRef.current.optionsTranslateY) }, children: [options && (jsx("div", { className: styles.options, children: options }, OPTIONS_KEY)), showDivider && jsx(Divider, {})] }, OPTIONS_CONTAINER_KEY))] }));
    }
    return null;
};

export { NavigationBarComponent };
//# sourceMappingURL=NavigationBarComponent.js.map
